import React, { useState } from 'react';
import { Bot, ArrowDown, ArrowUp, Copy, Check, FileText } from 'lucide-react';

interface CompactMessageProps {
  title: string;
  subtitle?: string;
  timestamp: string;
  avatar?: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
  showRawMarkup?: boolean;
  onToggleMarkup?: () => void;
  onCopy?: () => void;
  onLoadLog?: () => void;
  content?: string;
  cost?: string;
  costTooltip?: string;
}

/**
 * Renders a message in compact 4-line format with expandable details
 */
export const CompactMessage: React.FC<CompactMessageProps> = ({
  title,
  subtitle,
  timestamp,
  avatar,
  children,
  defaultOpen = false,
  showRawMarkup = false,
  onToggleMarkup,
  onCopy,
  onLoadLog,
  content,
  cost,
  costTooltip,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!content) return;
    
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      onCopy?.();
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="flex gap-4 items-start">
      {/* Avatar */}
      <div className="flex-shrink-0">
        {avatar || (
          <div className="w-8 h-8 rounded-full flex items-center justify-center bg-muted">
            <Bot className="h-5 w-5 text-muted-foreground" />
          </div>
        )}
      </div>

      {/* Compact content */}
      <div className="flex-1 min-w-0">
        {/* Title line */}
        <div className="text-sm font-semibold mb-1 text-foreground/50">
          {title}
        </div>

        {/* Subtitle line (if provided) */}
        {subtitle && (
          <div className="text-xs text-foreground/50 mb-2">
            {subtitle}
          </div>
        )}

        {/* Expandable details */}
        <details 
          className="mb-2" 
          open={isOpen} 
          onToggle={(e) => setIsOpen((e.target as HTMLDetailsElement).open)}
        >
          <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground select-none mb-2">
            {isOpen ? '▾' : '▸'} View Details
          </summary>
          <div className="mt-2">
            {children}
          </div>
        </details>

        {/* Timestamp line with Cost, Copy, Log, and Markup Toggle */}
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span>{timestamp}</span>
          {cost && (
            <span 
              className="font-mono"
              title={costTooltip}
            >
              {cost}
            </span>
          )}
          {content && (
            <button
              onClick={handleCopy}
              className="flex items-center cursor-pointer hover:opacity-80 transition-opacity"
              title="Copy message content"
            >
              {copied ? (
                <Check className="w-3 h-3 text-green-600 dark:text-green-400" />
              ) : (
                <Copy className="w-3 h-3" />
              )}
            </button>
          )}
          {onLoadLog && (
            <button
              onClick={onLoadLog}
              className="flex items-center cursor-pointer hover:opacity-80 transition-opacity"
              title="Load log between user prompt and this message to clipboard"
            >
              <FileText className="w-3 h-3" />
            </button>
          )}
          {onToggleMarkup && (
            <button
              onClick={onToggleMarkup}
              className="flex items-center gap-0.5 cursor-pointer hover:opacity-80 transition-opacity"
              title={showRawMarkup ? 'Switch to rendered view' : 'Switch to raw markup'}
            >
              {showRawMarkup ? (
                <>
                  <span className="text-xs">markup</span>
                  <ArrowUp className="w-3 h-3" />
                </>
              ) : (
                <>
                  <span className="text-xs">markdown</span>
                  <ArrowDown className="w-3 h-3" />
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
